import { useState } from 'react';
import { Modal, Input, Button, DropZone, Badge } from '../../../shared/ui';
import { useCreateProject, useQuickAnalysis, useGithubRepositories } from '../../../shared/api';
import { sileo } from 'sileo';
import { FolderPlus, AlertCircle, Code, CheckCircle, Trash2, ArrowRight, Github, Loader2, ChevronRight } from 'lucide-react';
import { useAnalysisStore } from '../../../shared/lib/store/analysisStore';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { QuickAnalysisResult } from '../../../entities/analysis';
import { normalizeLanguage, LANGUAGE_MAP, detectLanguageFromExtension } from '../../../shared/lib/language-utils';

interface ProjectFormProps {
    isOpen: boolean;
    onClose: () => void;
    project?: {
        id: string;
        name: string;
        description: string;
        language: string;
        branch: string;
    } | null;
}

const LANGUAGES = [
    'TypeScript',
    'JavaScript',
    'Python',
    'C#',
    'Java',
    'Go',
    'Rust',
    'Ruby',
    'PHP',
];

export function ProjectForm({ isOpen, onClose, project }: ProjectFormProps) {
    const { mutate: createProject, isPending } = useCreateProject();
    const { mutate: runAnalysis } = useQuickAnalysis();
    const { data: githubRepos, isLoading: isLoadingRepos, isError: isGithubError } = useGithubRepositories();
    
    const addLocalResult = useAnalysisStore(state => state.addResult);
    const qc = useQueryClient();
    const navigate = useNavigate();

    const [formData, setFormData] = useState({
        name: project?.name || '',
        description: project?.description || '',
        language: project?.language || 'TypeScript',
        branch: project?.branch || 'main',
        repositoryUrl: '',
        source: 'repo' as 'repo' | 'direct',
    });

    const [searchRepo, setSearchRepo] = useState('');

    const filteredRepos = (githubRepos || []).filter(repo => 
        repo.full_name.toLowerCase().includes(searchRepo.toLowerCase())
    );

    const [directCode, setDirectCode] = useState<{ name: string; content: string } | null>(null);
    const [analysisResult, setAnalysisResult] = useState<QuickAnalysisResult | null>(null);
    const [createdProjectId, setCreatedProjectId] = useState<string | null>(null);

    const [errors, setErrors] = useState<Record<string, string>>({});

    const isProcessing = isPending || (runAnalysis as any).isPending;

    const handleSubmit = (e: React.FormEvent) => {
        if (e) e.preventDefault();

        // Validation
        const newErrors: Record<string, string> = {};
        if (!formData.name.trim()) newErrors.name = 'Project name is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        if (!project) {
            createProject({
                name: formData.name,
                description: formData.description,
                repository_url: formData.repositoryUrl || null,
                default_branch: formData.branch,
            }, {
                onSuccess: (newProject: any) => {
                    const projectId = newProject.id || newProject.project_id;
                    setCreatedProjectId(projectId);
                    console.log('Project created:', { projectId, newProject });

                    sileo.success({
                        title: 'Project Created',
                        description: `Project "${formData.name}" has been created successfully.`,
                        icon: <FolderPlus className="size-3.5" />,
                    });

                    if (formData.source === 'direct' && directCode && projectId) {
                        sileo.action({
                            title: 'Starting Analysis',
                            description: 'Running initial analysis on your dropped code...',
                            icon: <Code className="size-3.5" />,
                        });

                        runAnalysis({
                            project_id: projectId,
                            snippets: [{
                                content: directCode.content,
                                language: normalizeLanguage(formData.language),
                                file_path: directCode.name,
                            }],
                        }, {
                            onSuccess: (data) => {
                                // Persist results to local store so they show up in stats
                                addLocalResult(projectId, directCode.name, data);
                                setAnalysisResult(data);

                                // Invalidate queries to refresh dashboard and charts
                                qc.invalidateQueries({ queryKey: ['analysis', 'project-summary', projectId] });
                                qc.invalidateQueries({ queryKey: ['reports', 'project-reports', projectId] });
                                qc.invalidateQueries({ queryKey: ['reports', 'projects'] });
                                qc.invalidateQueries({ queryKey: ['reports', 'dashboard'] });

                                sileo.success({
                                    title: 'Analysis Complete',
                                    description: `Initial analysis for "${formData.name}" is finished and saved.`,
                                    icon: <CheckCircle className="size-3.5" />,
                                });
                            },
                            onError: (err: any) => {
                                console.error('Immediate analysis failed:', err);
                                sileo.error({
                                    title: 'Analysis Failed',
                                    description: 'Project created, but initial analysis failed.',
                                    icon: <AlertCircle className="size-3.5" />,
                                });
                            }
                        });
                    } else {
                        onClose();
                    }
                },
                onError: (error: any) => {
                    sileo.error({
                        title: 'Creation Failed',
                        description: error.message || 'An error occurred while creating the project.',
                        icon: <AlertCircle className="size-3.5" />,
                    });
                }
            });
        } else {
            // TODO: Partial update support in backend/hooks if needed
            console.log('Update project:', formData);
            onClose();
        }
    };

    const handleChange = (field: string, value: string) => {
        setFormData((prev) => ({ ...prev, [field]: value }));
        // Clear error when user types
        if (errors[field]) {
            setErrors((prev) => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const handleSelectRepo = (repo: any) => {
        setFormData(prev => ({
            ...prev,
            name: repo.name,
            description: repo.description || `Repository ${repo.full_name}`,
            repositoryUrl: repo.html_url,
            branch: repo.default_branch || 'main'
        }));
        setSearchRepo('');
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title={project ? 'Edit Project' : 'Create New Project'}
            size="lg"
            footer={
                <>
                    {analysisResult ? (
                        <div className="flex justify-between w-full">
                            <Button variant="ghost" onClick={onClose} corners="asymmetric">
                                Close
                            </Button>
                            <Button
                                variant="primary"
                                onClick={() => {
                                    if (createdProjectId) navigate({ to: `/projects/$projectId`, params: { projectId: createdProjectId } });
                                    onClose();
                                }}
                                corners="asymmetric-inverse"
                            >
                                View Project Details
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                        </div>
                    ) : (
                        <>
                            <Button variant="ghost" onClick={onClose} corners="asymmetric" disabled={isProcessing}>
                                Cancel
                            </Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                corners="asymmetric-inverse"
                                disabled={isProcessing}
                            >
                                {isPending ? 'Creating...' : (runAnalysis as any).isPending ? 'Analyzing...' : (project ? 'Save Changes' : 'Create Project')}
                            </Button>
                        </>
                    )}
                </>
            }
        >
            <form onSubmit={handleSubmit} className="space-y-6">
                {analysisResult ? (
                    <div className="space-y-6">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-text-primary font-mono">Analysis Results</h3>
                            <div className="flex gap-2">
                                <Badge severity="info">{analysisResult.metrics.complexity} Complexity</Badge>
                                <Badge severity="info">{analysisResult.metrics.lines} Lines</Badge>
                            </div>
                        </div>

                        {analysisResult.issues.length === 0 ? (
                            <div className="flex items-center gap-2 p-4 bg-success/10 border border-success/30 rounded-asymmetric-md text-success text-sm font-mono">
                                <CheckCircle className="h-4 w-4" />
                                No issues found in this snippet!
                            </div>
                        ) : (
                            <div className="space-y-3 max-h-80 overflow-y-auto pr-2">
                                {analysisResult.issues.map((issue, idx) => (
                                    <div key={idx} className="p-4 bg-surface-hover border border-border rounded-asymmetric-sm space-y-2">
                                        <div className="flex items-center justify-between">
                                            <Badge severity={issue.severity}>{issue.severity.toUpperCase()}</Badge>
                                            <span className="text-xs text-text-muted font-mono">L{issue.line_start}-L{issue.line_end}</span>
                                        </div>
                                        <p className="text-sm font-medium text-text-primary">{issue.title}</p>
                                        <p className="text-xs text-text-muted line-clamp-2">{issue.description}</p>
                                        {issue.suggestion && (
                                            <div className="mt-2 p-2 bg-primary/5 border border-primary/20 rounded text-xs font-mono text-primary">
                                                <strong>Suggestion:</strong> {issue.suggestion}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                        <div className="rounded-asymmetric-md border border-primary/30 bg-primary/5 p-4">
                            <p className="text-sm text-text-secondary font-mono">
                                💡 <strong>Tip:</strong> These results have been saved to your project. You can view them anytime in the project details page.
                            </p>
                        </div>
                    </div>
                ) : (
                    <>
                        {/* Analysis Source Toggle */}
                        <div className="flex p-1 bg-surface-hover rounded-asymmetric-md border border-border mb-6">
                            <button
                                type="button"
                                onClick={() => handleChange('source', 'repo')}
                                className={`flex-1 py-2 px-4 text-xs font-mono rounded-asymmetric-sm transition-all ${formData.source === 'repo' ? 'bg-primary text-black font-bold shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                            >
                                GitHub Repository
                            </button>
                            <button
                                type="button"
                                onClick={() => handleChange('source', 'direct')}
                                className={`flex-1 py-2 px-4 text-xs font-mono rounded-asymmetric-sm transition-all ${formData.source === 'direct' ? 'bg-primary text-black font-bold shadow-sm' : 'text-text-muted hover:text-text-primary'}`}
                            >
                                Direct Code Snippets
                            </button>
                        </div>

                        {formData.source === 'repo' && (
                            <div className="space-y-4 mb-6 p-4 border border-border bg-surface-hover rounded-asymmetric-md">
                                <label className="block text-xs font-bold text-text-secondary font-mono uppercase tracking-wider">
                                    <Github className="inline-block h-3 w-3 mr-1" /> Quick Select Repository
                                </label>
                                
                                <div className="relative">
                                    <Input
                                        placeholder="Search your GitHub repos..."
                                        value={searchRepo}
                                        onChange={(e) => setSearchRepo(e.target.value)}
                                        icon={isLoadingRepos ? <Loader2 className="h-4 w-4 animate-spin" /> : <Github className="h-4 w-4" />}
                                    />
                                    
                                    {searchRepo && (
                                        <div className="absolute z-10 w-full mt-1 max-h-48 overflow-y-auto bg-surface border border-border rounded-asymmetric-sm shadow-xl">
                                            {filteredRepos.length > 0 ? (
                                                filteredRepos.map(repo => (
                                                    <button
                                                        key={repo.id}
                                                        type="button"
                                                        onClick={() => handleSelectRepo(repo)}
                                                        className="w-full px-4 py-2 text-left hover:bg-primary/10 text-sm font-mono flex items-center justify-between group"
                                                    >
                                                        <span className="truncate">{repo.full_name}</span>
                                                        <ChevronRight className="h-3 w-3 text-text-muted group-hover:text-primary" />
                                                    </button>
                                                ))
                                            ) : (
                                                <div className="px-4 py-2 text-xs text-text-muted font-mono">
                                                    {isLoadingRepos ? 'Loading...' : 'No repositories found'}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {isGithubError && (
                                    <p className="text-[10px] text-error font-mono">⚠️ Could not fetch repositories. Is your token valid?</p>
                                )}
                            </div>
                        )}

                        {/* Project Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-text-primary font-mono mb-2">
                                Project Name *
                            </label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="e.g. My Awesome Project"
                                value={formData.name}
                                onChange={(e) => handleChange('name', e.target.value)}
                                error={errors.name}
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-text-primary font-mono mb-2">
                                Description *
                            </label>
                            <textarea
                                id="description"
                                rows={3}
                                placeholder="Brief description of your project..."
                                value={formData.description}
                                onChange={(e) => handleChange('description', e.target.value)}
                                className={`
              w-full px-4 py-3 rounded-asymmetric-md
              bg-surface border border-border
              text-text-primary font-mono text-sm
              placeholder:text-text-muted
              focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
              transition-all duration-200
              ${errors.description ? 'border-error focus:ring-error' : ''}
            `}
                            />
                            {errors.description && (
                                <p className="mt-1 text-sm text-error font-mono">{errors.description}</p>
                            )}
                        </div>

                        {/* Language & Branch */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="language" className="block text-sm font-medium text-text-primary font-mono mb-2">
                                    Primary Language
                                </label>
                                <select
                                    id="language"
                                    value={formData.language}
                                    onChange={(e) => handleChange('language', e.target.value)}
                                    className="
                w-full px-4 py-3 rounded-asymmetric-md
                bg-surface border border-border
                text-text-primary font-mono text-sm
                focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent
                transition-all duration-200
                cursor-pointer
              "
                                >
                                    {LANGUAGES.map((lang) => (
                                        <option key={lang} value={lang}>
                                            {lang}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label htmlFor="branch" className="block text-sm font-medium text-text-primary font-mono mb-2">
                                    Default Branch
                                </label>
                                <Input
                                    id="branch"
                                    type="text"
                                    placeholder="main"
                                    value={formData.branch}
                                    onChange={(e) => handleChange('branch', e.target.value)}
                                />
                            </div>
                        </div>

                        {/* Repository URL (conditional) */}
                        {formData.source === 'repo' ? (
                            <div>
                                <label htmlFor="repositoryUrl" className="block text-sm font-medium text-text-primary font-mono mb-2">
                                    Repository URL *
                                </label>
                                <Input
                                    id="repositoryUrl"
                                    type="url"
                                    placeholder="https://github.com/username/repo"
                                    value={formData.repositoryUrl}
                                    onChange={(e) => handleChange('repositoryUrl', e.target.value)}
                                    error={errors.repositoryUrl}
                                />
                                <p className="mt-1 text-[10px] text-text-muted font-mono italic">
                                    Enter URL or use the quick selector above.
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {directCode ? (
                                    <div className="rounded-asymmetric-md border border-primary/30 bg-primary/5 p-4 flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 bg-surface rounded-lg">
                                                <Code className="h-4 w-4 text-primary" />
                                            </div>
                                            <div>
                                                <p className="text-sm font-mono text-text-primary">{directCode.name}</p>
                                                <p className="text-[10px] font-mono text-text-muted uppercase">Ready for analysis</p>
                                            </div>
                                        </div>
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => setDirectCode(null)}
                                            className="text-error hover:text-error hover:bg-error/10"
                                        >
                                            <Trash2 className="h-4 w-4" />
                                        </Button>
                                    </div>
                                ) : (
                                    <DropZone
                                        onFileRead={(name, content) => {
                                            setDirectCode({ name, content });
                                            // Auto-detect language
                                            const detected = detectLanguageFromExtension(name);
                                            // Find display name for the detected language ID
                                            const displayName = Object.keys(LANGUAGE_MAP).find(key => LANGUAGE_MAP[key] === detected) || 'TypeScript';
                                            handleChange('language', displayName);
                                        }}
                                        className="h-40"
                                    />
                                )}
                            </div>
                        )}
                    </>
                )}
            </form>
        </Modal>
    );
}
